<script setup lang="ts">
/**
 * The dialog around `CheckoutPaymentWaitBody`.
 *
 * All of the content — the states, the "checked Xs ago" ticker, the buttons —
 * lives in that component, because the commit sheet shows the same thing as its
 * final step and a second dialog over the first is not a handover, it is two
 * overlays. What is left here is the shell, and it is still the surface for a
 * balance TOP-UP: that flow is reached from the signed-in console with no
 * commit sheet around it.
 */
const props = defineProps<{
  open: boolean
  /** `order` = a new instance awaiting payment; `topup` = a balance recharge. */
  mode: 'order' | 'topup'
  invoiceExternalId: string
  /** Invoice total in cents (string). */
  totalCents: string
  grossCents?: string | null
  discountCents?: string | null
  /** Subscription balance taken off the gross — see the body. */
  balanceAppliedCents?: string | null
  /** What the minimum recharge added above the net charge — see the body. */
  minimumRechargeCreditCents?: string | null
  /** Add-to-existing itemisation — see the body. */
  chargedHours?: number | null
  cycleEndsAt?: string | null
  termCents?: string | null
  /** waiting → paid → provisioning are the happy path; failed/expired are ends. */
  state: 'waiting' | 'paid' | 'provisioning' | 'failed' | 'expired'
  checking?: boolean
  /** Epoch ms of the last completed check, for the "checked Xs ago" ticker. */
  lastCheckedAt?: number | null
  /** When an unpaid order stops being payable (ISO). */
  expiresAt?: string | null
  /** Something went wrong on the last check — shown inline, not as a toast. */
  errorMessage?: string | null
  /** The invoice is being opened — see the body, which owns the reasoning. */
  openingInvoice?: boolean
}>()

const emit = defineEmits<{
  'update:open': [boolean]
  check: []
  'open-invoice': []
}>()

/** Everything except `open`, which is the shell's own business. */
const bodyProps = computed(() => {
  const { open: _open, ...rest } = props
  return rest
})
</script>

<template>
  <UModal
    :open="open"
    :dismissible="false"
    :ui="{ content: 'sm:max-w-lg' }"
    @update:open="emit('update:open', $event)"
  >
    <template #content>
      <CheckoutPaymentWaitBody
        v-bind="bodyProps"
        show-close
        @close="emit('update:open', false)"
        @check="emit('check')"
        @open-invoice="emit('open-invoice')"
      />
    </template>
  </UModal>
</template>
